import type { StateCreator } from 'zustand';
import type { StoreState } from './index';
import type { IgConnection, IgPublishLog, IgPublishSSEEvent } from '../types';
import { igPublishingApi } from '../services/igPublishingApi';

export interface IgPublishingSlice {
  igConnections: IgConnection[];
  igPublishHistory: IgPublishLog[];
  igPublishHistoryTotal: number;
  igPublishStatus: Record<number, IgPublishLog[]>;
  igLoading: boolean;
  igConnectionsLoaded: boolean;

  fetchIgConnections: () => Promise<void>;
  startIgOAuth: (brandId?: number, channelId?: number, forceNew?: boolean) => Promise<void>;
  disconnectIg: (id: number) => Promise<void>;
  publishToIg: (channelId: number, genId: number, postType: string, scheduledAt?: string) => Promise<{ log_id: number }>;
  fetchPublishHistory: (connectionId?: number, limit?: number, offset?: number) => Promise<void>;
  fetchPublishStatus: (genId: number) => Promise<void>;
  handleIgPublishSSE: (data: IgPublishSSEEvent) => void;
}

export const createIgPublishingSlice: StateCreator<StoreState, [], [], IgPublishingSlice> = (set, get) => ({
  igConnections: [],
  igPublishHistory: [],
  igPublishHistoryTotal: 0,
  igPublishStatus: {},
  igLoading: false,
  igConnectionsLoaded: false,

  fetchIgConnections: async () => {
    set({ igLoading: true });
    try {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 10000)
      );
      const connections = await Promise.race([igPublishingApi.getConnections(), timeout]);
      set({ igConnections: connections, igLoading: false, igConnectionsLoaded: true });
    } catch {
      set({ igLoading: false, igConnectionsLoaded: true });
    }
  },

  startIgOAuth: async (brandId?: number, channelId?: number, forceNew?: boolean) => {
    const { auth_url } = await igPublishingApi.startOAuth(brandId, channelId, forceNew);
    window.location.href = auth_url;
  },

  disconnectIg: async (id) => {
    await igPublishingApi.disconnect(id);
    set((s) => ({ igConnections: s.igConnections.filter((c) => c.id !== id) }));
  },

  publishToIg: async (channelId, genId, postType, scheduledAt) => {
    const state = get();
    const channel = (state.strategyBrand ?? state.currentBrand)?.channels?.find(c => c.id === channelId);
    if (!channel?.ig_connection_id) {
      throw new Error('NO_IG_CONNECTION');
    }
    const result = await igPublishingApi.publish({
      channel_id: channelId,
      generation_id: genId,
      post_type: postType,
      scheduled_at: scheduledAt ?? null,
    });
    // Add optimistic publishing entry to status
    set((s) => ({
      igPublishStatus: {
        ...s.igPublishStatus,
        [genId]: [
          ...(s.igPublishStatus[genId] || []),
          {
            id: result.log_id,
            connection_id: channel.ig_connection_id!,
            channel_id: channelId,
            generation_id: genId,
            post_type: postType as 'image' | 'carousel',
            status: scheduledAt ? 'scheduled' : 'publishing',
            permalink: null,
            error_message: null,
            created_at: new Date().toISOString(),
            published_at: null,
            scheduled_at: scheduledAt ?? null,
          },
        ],
      },
    }));
    return result;
  },

  fetchPublishHistory: async (connectionId, limit = 20, offset = 0) => {
    try {
      const result = await igPublishingApi.getPublishHistory({ connection_id: connectionId, limit, offset });
      set({ igPublishHistory: result.items, igPublishHistoryTotal: result.total });
    } catch {
      // ignore
    }
  },

  fetchPublishStatus: async (genId) => {
    try {
      const logs = await igPublishingApi.getPublishStatus(genId);
      set((s) => ({ igPublishStatus: { ...s.igPublishStatus, [genId]: logs } }));
    } catch {
      // ignore
    }
  },

  handleIgPublishSSE: (data) => {
    const genId = data.generation_id;
    set((s) => {
      // Update publish status
      const updated = { ...s.igPublishStatus };
      if (genId != null && updated[genId]) {
        updated[genId] = updated[genId].map((log) =>
          log.id === data.log_id
            ? {
                ...log,
                status: data.status,
                permalink: data.permalink || null,
                error_message: data.error || null,
                published_at: data.status === 'success' ? new Date().toISOString() : null,
              }
            : log
        );
      }
      // Update history if present
      const newHistory = s.igPublishHistory.map((log) =>
        log.id === data.log_id
          ? {
              ...log,
              status: data.status,
              permalink: data.permalink || null,
              error_message: data.error || null,
            }
          : log
      );
      return { igPublishStatus: updated, igPublishHistory: newHistory };
    });
  },
});
